import React, {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type FormValues } from '../ui/form/types';

import { useFocusedField } from './focused-field-provider';

type Events = {
  submitSuccessCounter: number;
  submittedValues: FormValues;
  fireSubmitSuccess: (values?: FormValues) => void;
};

const EventsContext = createContext<Events | undefined>(undefined);

export type EventsProviderProps = {
  children: ReactNode;
};

export const EventsProvider = ({ children }: EventsProviderProps) => {
  const [submitSuccessCounter, setSubmitSuccessCounter] = useState(0);
  const [submittedValues, setSubmittedValues] = useState<FormValues>({});

  const value: Events = useMemo(
    () => ({
      submitSuccessCounter,
      submittedValues,
      fireSubmitSuccess: (values) => {
        setSubmitSuccessCounter((previous) => previous + 1);
        setSubmittedValues(values || {});
      },
    }),
    [submitSuccessCounter, submittedValues],
  );

  return (
    <EventsContext.Provider value={value}>{children}</EventsContext.Provider>
  );
};

const useEvents = () => {
  const context = useContext(EventsContext);

  if (context === undefined) {
    throw new Error('useEvents must be used within a EventsProvider');
  }

  return context;
};

export const useSubmitSuccessTrigger = () => {
  const { fireSubmitSuccess } = useEvents();
  return fireSubmitSuccess;
};

export const useSubmitSuccessEvent = (
  onSubmitSuccess: (values: FormValues) => void,
) => {
  const { submitSuccessCounter, submittedValues = {} } = useEvents();
  const previousCounter = useRef(submitSuccessCounter);

  useEffect(() => {
    if (previousCounter.current !== submitSuccessCounter) {
      previousCounter.current = submitSuccessCounter;
      onSubmitSuccess(submittedValues);
    }
  }, [submitSuccessCounter, onSubmitSuccess, submittedValues]);
};

export const useFocusGroupFieldEvent = (
  groupFieldId: string | undefined,
  getFocusedChildFieldId: () => string | undefined,
) => {
  const { focusedFieldId, setFocusedFieldId } = useFocusedField();

  useEffect(() => {
    if (groupFieldId && focusedFieldId === groupFieldId) {
      const fieldId = getFocusedChildFieldId();
      if (fieldId) {
        setFocusedFieldId(fieldId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupFieldId, focusedFieldId, setFocusedFieldId]);
};

export const useFocusFieldEvent = (
  onFocusField: () => void,
  fieldId?: string,
) => {
  const { focusedFieldId, setFocusedFieldId } = useFocusedField();

  useEffect(() => {
    if (fieldId && focusedFieldId && focusedFieldId === fieldId) {
      onFocusField();
      setFocusedFieldId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId, focusedFieldId, setFocusedFieldId]);
};
